import clsx from 'clsx'

import { FvButton, Icon } from '@fv/client-components'
import {
  currencyToString,
  generateCharge,
  getAmount,
  getManualChargeOptions,
  isManagedTax,
  parseCurrency,
} from '@fv/client-core'
import type { ChargeName, Currency, ExchangeRateMap } from '@fv/models'
import { chargeNameLabel } from '@fv/models/core'

import { PriceInput } from '@/components/inputs/PriceInput'
import { SelectInput } from '@/components/inputs/SelectInput'
import { TextField } from '@/components/TextField'
import type { Opportunity } from '@/types/Opportunity'

import type { ChargeModel, Model } from './types'

type Props = {
  onChange: (quote: Model) => void
  model?: Model
  opportunity?: Opportunity
  currency?: Currency
  exchangeRates?: ExchangeRateMap
}
export const ChargeBreakdownControls = ({
  model,
  onChange,
  opportunity,
  currency,
  exchangeRates,
}: Props) => {
  const total = getAmount(model)
  const locations = opportunity?.locations
  const charges = model.charges?.filter(
    c => c.name !== 'linehaul' && !isManagedTax(c.name, currency, locations),
  )
  const taxCharges =
    model.charges?.filter(c => isManagedTax(c.name, currency, locations)) ?? []
  const exchangeRate =
    currency && currency !== 'usd' ? exchangeRates?.[currency] : undefined
  const usdEquivalent = exchangeRate && total ? total / exchangeRate : null

  const handleAddClick = () => {
    onChange({
      ...model,
      charges: [...model.charges, { ...generateCharge() }],
    })
  }

  return (
    <div>
      {charges && charges.length > 0 && (
        <div className="-mx-4 mt-3 [&>div]:py-1 px-4 bg-fv-blue-100 p-2 border-t  border-fv-blue-200">
          {charges.map((c, ix) => (
            <ChargeBreakdownRow
              key={ix}
              charge={c}
              opportunity={opportunity}
              currency={currency}
              onChange={updatedCharge =>
                onChange({
                  ...model,
                  charges: model.charges?.map(charge =>
                    charge.id === updatedCharge.id ? updatedCharge : charge,
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  ...model,
                  charges: model.charges?.filter(charge => charge.id !== c.id),
                })
              }
            />
          ))}
        </div>
      )}
      <div className="flex items-center gap-x-2 justify-between py-3 border-t border-dotted border-fv-blue-300  bg-fv-blue-50 -mx-4 px-4 border-b">
        <FvButton icon="plus" onClick={handleAddClick}>
          Add an additional charge
        </FvButton>
        <div className="flex flex-col items-end">
          <div className="divide divide-y divide-dotted divide-gray-300 text-sm italic mb-2">
            {taxCharges.map(t => (
              <div key={t.id} className="text-sm py-1 mr-2">
                <span>{chargeNameLabel(t.name)} = </span>
                <span>
                  ${currencyToString(t.amount)}{' '}
                  {currency ? currency.toUpperCase() : ''}
                </span>
              </div>
            ))}
          </div>
          <div className="whitespace-nowrap flex">
            <span>Total price =&nbsp;</span>
            <span className="py-2 px-3 bg-lime-50  border border-lime-300 inline-flex gap-x-1">
              <span className="font-medium text-lg">
                ${currencyToString(total)}
              </span>
              <span className="text-xs self-start mt-[.2rem]">
                {currency ? currency.toUpperCase() : ''}
              </span>
            </span>
          </div>
          {usdEquivalent !== null && (
            <div className="text-xs italic mt-2">
              &asymp; ${currencyToString(usdEquivalent)} USD (EXR:{' '}
              {exchangeRate.toFixed(4)})
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type RowProps = {
  charge: ChargeModel
  onRemove: () => void
  onChange: (value: ChargeModel) => void
  opportunity?: Opportunity
  currency?: Currency
}
const ChargeBreakdownRow = ({
  charge,
  onRemove,
  onChange,
  opportunity,
  currency,
}: RowProps) => {
  const isFuelCharge =
    charge.name === 'fuel' || charge.name === 'fuel surcharge'
  const isBreakthroughFuel = opportunity?.fuel?.provider === 'breakthrough'
  const shouldLockFuelPrice = isFuelCharge && isBreakthroughFuel
  const isLocked = shouldLockFuelPrice
  const isDeduction = charge.amount < 0
  const getPriceInputValue = (): string => {
    if (shouldLockFuelPrice && opportunity?.fuel?.cost) {
      return currencyToString(opportunity.fuel.cost)
    }
    if (charge.amount) {
      return currencyToString(Math.abs(charge.amount))
    }
    return ''
  }

  const baseOptions = getManualChargeOptions(currency, opportunity?.locations)
  const options = isBreakthroughFuel
    ? baseOptions.filter(o => isFuelCharge || !o.value.includes('fuel'))
    : baseOptions

  return (
    <div className="flex items-center gap-x-2">
      <SelectInput
        className={clsx('w-auto shrink-0', {
          'bg-fv-gray-200': isLocked,
        })}
        name={`charge-type-${charge.id}`}
        onChange={e =>
          onChange({ ...charge, name: e.target.value as ChargeName })
        }
        options={options}
        value={charge.name}
        disabled={isLocked}
        required
      />
      {shouldLockFuelPrice && (
        <i className="text-sm">provided by Breakthrough Fuel</i>
      )}

      {charge.name === 'other' && (
        <TextField
          value={charge.providerDetail || ''}
          name={`charge-desc-${charge.id}`}
          placeholder="Enter charge description"
          onChange={e =>
            onChange({ ...charge, providerDetail: e.target.value })
          }
        />
      )}
      <div className="h-0 flex-1 border-t border-dotted border-fv-blue-300" />

      {!isLocked && (
        <FvButton
          icon="trash"
          iconClass="text-fv-orange"
          className="mr-2"
          onClick={onRemove}
        />
      )}
      <div className="input-group input-group--flex b400:basis-full mb-0 ml-auto">
        <div className="input-group-addon">
          {isDeduction && <span className="color-dark mr-0.5">-</span>}
          <Icon icon="dollar-sign" className="color-dark" />
        </div>
        <PriceInput
          name={`charge-amount-${charge.id}`}
          className={clsx('form-control b400:w-full w-[5rem] text-right', {
            'bg-fv-gray-200': isLocked,
          })}
          disabled={isLocked}
          value={getPriceInputValue()}
          onChange={e => {
            const amount = parseCurrency(e.target.value)
            onChange({ ...charge, amount: isNaN(amount) ? 0 : amount })
          }}
          required={true}
        />
      </div>
    </div>
  )
}
